import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { findShipping, SHIPPING_OPTIONS } from "./shipping";

const SHIPPING_IDS = SHIPPING_OPTIONS.map((o) => o.id) as [string, ...string[]];

/** Buyer sets shipping method on their pending order. */
export const setOrderShipping = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      orderId: z.string().uuid(),
      shippingMethod: z.enum(SHIPPING_IDS),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const opt = findShipping(data.shippingMethod)!;
    const { error } = await context.supabase.rpc("set_order_shipping" as never, {
      _order_id: data.orderId,
      _method: opt.id,
      _cost_pence: opt.costPence,
    } as never);
    if (error) throw new Error(error.message);
    return { ok: true, shippingCostPence: opt.costPence, shippingMethod: opt.id };
  });

/** Create a Stripe Checkout Session (Separate Charges & Transfers: platform captures funds; seller payout via manual Transfer after buyer confirmation). */
export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      orderId: z.string().uuid(),
      origin: z.string().url(),
      shippingMethod: z.enum(SHIPPING_IDS).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Optionally set shipping in the same call.
    if (data.shippingMethod) {
      const opt = findShipping(data.shippingMethod)!;
      const { error: sErr } = await supabase.rpc("set_order_shipping" as never, {
        _order_id: data.orderId,
        _method: opt.id,
        _cost_pence: opt.costPence,
      } as never);
      if (sErr) throw new Error(sErr.message);
    }

    const { data: order, error } = await supabase
      .from("orders")
      .select("id,buyer_id,seller_id,amount_pence,commission_pence,seller_payout_pence,shipping_method,shipping_cost_pence,currency,status,auction_id,auctions(title,images)")
      .eq("id", data.orderId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!order) throw new Error("Order not found");
    if (order.buyer_id !== userId) throw new Error("You are not the buyer for this order");
    if (order.status !== "pending_payment") throw new Error("Order is not awaiting payment");
    if (!order.shipping_method) throw new Error("Please choose a shipping method first");

    // Look up seller's connected account (required for Connect payments).
    const { data: seller, error: sErr } = await supabase
      .from("profiles")
      .select("stripe_account_id,stripe_charges_enabled,stripe_payouts_enabled,stripe_details_submitted")
      .eq("id", order.seller_id)
      .maybeSingle();
    if (sErr) throw new Error(sErr.message);
    if (!seller?.stripe_account_id || !seller.stripe_charges_enabled || !seller.stripe_payouts_enabled) {
      throw new Error("Seller has not completed Stripe onboarding — payment cannot proceed");
    }

    const { getStripe } = await import("./stripe-checkout.server");
    const stripe = getStripe();

    const auc = order.auctions as unknown as { title: string; images: string[] | null } | null;
    const title = auc?.title ?? "BidNova Auctions Auction";
    const image = auc?.images?.[0];
    const currency = (order.currency ?? "gbp").toLowerCase();

    const itemAmount = order.amount_pence;
    const shippingAmount = order.shipping_cost_pence ?? 0;
    const total = itemAmount + shippingAmount;
    // Platform fee applies to item total (not shipping), matching commission_pence.
    const applicationFee = order.commission_pence;

    const lineItems: Array<{
      quantity: number;
      price_data: {
        currency: string;
        unit_amount: number;
        product_data: { name: string; images?: string[]; metadata?: Record<string, string> };
      };
    }> = [
      {
        quantity: 1,
        price_data: {
          currency,
          unit_amount: itemAmount,
          product_data: {
            name: title,
            images: image ? [image] : undefined,
            metadata: { auction_id: order.auction_id },
          },
        },
      },
    ];
    if (shippingAmount > 0) {
      const shipOpt = findShipping(order.shipping_method);
      lineItems.push({
        quantity: 1,
        price_data: {
          currency,
          unit_amount: shippingAmount,
          product_data: { name: `Shipping — ${shipOpt?.label ?? order.shipping_method}` },
        },
      });
    }

    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      payment_method_types: ["card"],
      line_items: lineItems,
      // Collect a real shipping address + phone so the seller can print a
      // Royal Mail Click & Drop label from the exported CSV.
      shipping_address_collection: {
        allowed_countries: [
          "GB", "IE", "US", "CA", "AU", "NZ",
          "FR", "DE", "IT", "ES", "NL", "BE", "SE", "NO", "DK", "FI", "PT", "AT", "CH",
          "JP",
        ],
      },
      phone_number_collection: { enabled: true },
      payment_intent_data: {
        // Separate Charges & Transfers: charge goes to the platform account
        // in full. No application_fee_amount, no transfer_data.destination.
        // Seller payout is created later via releaseOrderPayoutViaStripe().
        transfer_group: `order_${order.id}`,
        metadata: {
          order_id: order.id,
          buyer_id: order.buyer_id,
          seller_id: order.seller_id,
          shipping_method: order.shipping_method ?? "",
        },
      },
      metadata: {
        order_id: order.id,
        buyer_id: order.buyer_id,
        seller_id: order.seller_id,
        shipping_method: order.shipping_method ?? "",
      },
      success_url: `${data.origin}/orders/success?order_id=${order.id}&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${data.origin}/orders`,
    });

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("orders")
      .update({
        stripe_session_id: session.id,
        stripe_checkout_url: session.url,
        stripe_application_fee_pence: applicationFee,
        stripe_destination_account_id: null,
      })
      .eq("id", order.id);
    await supabaseAdmin.rpc("_log_payment_event" as never, {
      _order_id: order.id,
      _event_type: "checkout_session_created",
      _actor_id: userId,
      _amount_pence: total,
      _metadata: {
        session_id: session.id,
        mode: "stripe_separate_charges_and_transfers",
        platform_fee_pence: applicationFee,
        shipping_cost_pence: shippingAmount,
        shipping_method: order.shipping_method,
        transfer_group: `order_${order.id}`,
      },
    } as never);

    return { url: session.url, sessionId: session.id };
  });

/** Verify a completed Stripe session (fallback when webhook isn't wired). */
export const verifyCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ orderId: z.string().uuid(), sessionId: z.string() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { getStripe } = await import("./stripe-checkout.server");
    const stripe = getStripe();
    const session = await stripe.checkout.sessions.retrieve(data.sessionId);

    if (session.metadata?.order_id !== data.orderId) throw new Error("Session/order mismatch");
    if (session.metadata?.buyer_id !== userId) throw new Error("Not your session");
    if (session.payment_status !== "paid") {
      return { paid: false, status: session.payment_status };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: order } = await supabaseAdmin
      .from("orders").select("status").eq("id", data.orderId).maybeSingle();
    if (order && order.status === "pending_payment") {
      await supabaseAdmin
        .from("orders")
        .update({
          status: "paid",
          paid_at: new Date().toISOString(),
          stripe_payment_intent_id: (session.payment_intent as string) ?? null,
        })
        .eq("id", data.orderId);
      await supabaseAdmin.rpc("_log_payment_event" as never, {
        _order_id: data.orderId,
        _event_type: "payment_confirmed",
        _actor_id: userId,
        _amount_pence: session.amount_total ?? null,
        _metadata: { source: "verify_session", session_id: session.id },
      } as never);
      await supabaseAdmin.rpc("finalize_paid_order" as never, { _order_id: data.orderId } as never);

      // Generate invoices and send payment-confirmation emails with the download link.
      try {
        const { ensureInvoicesForOrder, getSignedInvoiceUrl } = await import("./invoice.server");
        const invoices = await ensureInvoicesForOrder(data.orderId);
        const [buyerUrl, sellerUrl] = await Promise.all([
          getSignedInvoiceUrl(invoices.buyer.pdf_path),
          getSignedInvoiceUrl(invoices.seller.pdf_path),
        ]);
        const { data: o } = await supabaseAdmin
          .from("orders")
          .select("buyer_id,seller_id,amount_pence,shipping_cost_pence,currency,auctions(title)")
          .eq("id", data.orderId).maybeSingle();
        if (o) {
          const currency = ((o.currency ?? "GBP") as string).toUpperCase();
          const symbol = currency === "GBP" ? "£" : currency + " ";
          const total = o.amount_pence + (o.shipping_cost_pence ?? 0);
          const money = (p: number) =>
            symbol + (p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const title = (o.auctions as { title?: string } | null)?.title ?? "your order";
          await supabaseAdmin.rpc("send_email" as never, {
            _template: "order-paid",
            _user_id: o.buyer_id,
            _key: `order-paid:buyer:${data.orderId}`,
            _data: { auctionTitle: title, amount: money(total), invoiceUrl: buyerUrl, invoiceNumber: invoices.buyer.invoice_number },
          } as never);
          await supabaseAdmin.rpc("send_email" as never, {
            _template: "payment-received-seller",
            _user_id: o.seller_id,
            _key: `payment-received:seller:${data.orderId}`,
            _data: { auctionTitle: title, amount: money(o.amount_pence), invoiceUrl: sellerUrl, invoiceNumber: invoices.seller.invoice_number },
          } as never);
        }
      } catch (e) {
        console.error("[verify-session] invoice/email step failed", data.orderId, (e as Error).message);
      }
    }
    return { paid: true, status: "paid" };
  });
